import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
// import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Formik, FormikHelpers } from 'formik';
import { IInvitation } from 'modules/settings/management/manage-team/components/types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IncidentService } from 'services/IncidentService';
import { ManageTeamService } from 'services/ManageTeamService';
import {
  AppFormikDateTimePicker,
  AppFormikButtonTiles,
  // AppFormikDatePicker,
  AppFormikSelect,
  AppFormikSelectWithKeys,
  AppFormikTextArea,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import ScrollToError from 'shared/components/ScrollToError';
import { Toast, Utils } from 'shared/helpers';
import { PaginationActions } from 'state/pagination/Action';
import { IAppState } from 'state/RootReducer';
import { IncidentScheme } from 'validators/IncidentScheme';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import dayjs from 'dayjs';

const AddIncidents: React.FC = () => {
  const navigate = useNavigate();
  const { incidentId } = useParams();
  const dispatch = useDispatch();
  const teamsPagination = useSelector(
    (state: IAppState) => state.paginationState.teams
  );
  const [teamMembers, setTeamMembers] = useState<IInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [incidentDetail, setIncidentDetail] = useState({
    tabStatus: [true, false, false],
    title: '',
    description: '',
    incident_source: 'Human Input',
    incident_type: 'Privacy',
    severity: 'Moderate',
    business_owner: '',
    contains_personal_data: false,
    assigned_to: null,
    status: 'Detected',
    detected_at: '',
    data_subjects_impacted: 2,
    dpo_notified_at: '',
    regulatory_notified_at: '',
    regulatory_notification_reference: '',
    internal_escalation_level: '',
    containment_actions: '',
    root_cause: '',
    root_cause_report: '',
    operational_disruption_level: '',
    corrective_actions: '',
    harm_description: '',
    compliance_impact: '',
    recurrence_risk: '',
    retest_results: '',
    lessons_learned: '',
    reported_by: '',
  });
  const isEdit = useMemo(() => !!incidentId, [incidentId]);

  // API Calls
  const loadDetail = (id: string): void => {
    setIsLoading(true);
    IncidentService.getDetail(id)
      .then((response) => {
        const { data } = response.data;
        if (data) {
          data.assigned_to = data.assigned_to?._id;
          setIncidentDetail({ ...data, tabStatus: [true, false, false] });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        navigate(-1);
      });
  };
  const loadTeamMembers = (): void => {
    const queryString = new URLSearchParams({
      offset: String(teamsPagination.offset),
      limit: String(teamsPagination.pageLimit),
      search: teamsPagination.search,
    }).toString();
    ManageTeamService.teamMembers(queryString).then((response) => {
      const { user_list, pagination } = response.data;
      if (user_list.length > 0) {
        setTeamMembers(user_list);

        dispatch(
          PaginationActions.setPagination({
            teams: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      }
    });
  };
  const createIncident = (payload: any, callback: any): void => {
    IncidentService.create(payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };
  const updateIncident = (payload: any, callback: any): void => {
    IncidentService.update(`${incidentId}`, payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  // UI Actions
  const actionOnSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    if (isEdit) {
      updateIncident(formValues, (response: any) => {
        formik.setSubmitting(false);
        if (typeof response === 'object') {
          Toast.showSuccessMessage(
            'The incident has been successfully created.'
          );
          navigate(-1);
        }
      });
    } else {
      createIncident(formValues, (response: any) => {
        formik.setSubmitting(false);
        if (typeof response === 'object') {
          Toast.showSuccessMessage(
            'The incident has been successfully updated.'
          );
          navigate(-1);
        }
      });
    }
  };

  // Hooks
  useEffect(() => {
    loadTeamMembers();
  }, []);

  useEffect(() => {
    if (incidentId) {
      loadDetail(incidentId);
    }
  }, [incidentId]);

  // UI Elements
  return (
    <>
      {isLoading && (
        <Box className="empty-content">
          I<CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <>
          <PageHeader
            title={isEdit ? 'Edit Incident' : 'Report New Incident'}
            description={
              isEdit ? '' : 'Report a new incident for tracking and resolution'
            }
          />
          <Box className="incident-management add-incidents-wrapper">
            <Formik
              initialValues={incidentDetail}
              validationSchema={IncidentScheme}
              enableReinitialize
              validateOnMount
              onSubmit={(formValues, formik) =>
                actionOnSubmit(formValues, formik)
              }
            >
              {({ values, isSubmitting, handleSubmit, setFieldValue }) => (
                <>
                  <ScrollToError
                    callback={() => {
                      setFieldValue('tabStatus', [true, false, false]);
                    }}
                  />

                  <AppFormikButtonTiles
                    variant="outlined"
                    name="tabStatus"
                    size="small"
                    label=""
                    disabled={false}
                    multichoice={false}
                    className="app-group-button mb-5"
                    buttons={[
                      {
                        value: 'Basic Detail',
                      },
                      {
                        value: 'Investigation',
                      },
                      {
                        value: 'Resolution',
                      },
                    ]}
                    outerLabel
                  />
                  {values.tabStatus[0] && (
                    <Grid className="incident-wrapper" spacing={2} container>
                      <Grid item xs={12}>
                        <AppFormikTextField
                          name="title"
                          placeholder="Brief descriptive title"
                          label="Incident Title*"
                          outerLabel
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="description"
                          placeholder="Detailed description of the incident"
                          label="Description"
                          outerLabel
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikSelect
                          name="incident_source"
                          className="rework-selection"
                          notched={false}
                          labelId="fix-selection-label"
                          testid="solicitor-dropdown"
                          MenuProps={{
                            className: 'menu-fix-selection single-fix-rework',
                          }}
                          outerLabel
                          placeholder="Select Source"
                          notEmphasized
                          notSelectablePlaceholder
                          label="Incident Source"
                          items={[
                            'Automated Monitoring',
                            'Human Input',
                            'External Complaint',
                            'Audit',
                            'Internal',
                            'Customer',
                            'Third Party',
                            'Regulatory',
                            'Public',
                          ]}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikSelect
                          name="incident_type"
                          className="rework-selection"
                          notched={false}
                          labelId="fix-selection-label"
                          testid="solicitor-dropdown"
                          MenuProps={{
                            className: 'menu-fix-selection single-fix-rework',
                          }}
                          outerLabel
                          placeholder="Select Type"
                          notEmphasized
                          notSelectablePlaceholder
                          label="Incident Type"
                          items={[
                            'Privacy',
                            'Ethical',
                            'Performance',
                            'Regulatory',
                            'System Failure',
                            'Data Breach',
                            'Performance Issue',
                            'Hallucination',
                            'Bias/Fairness',
                            'Content Moderation',
                            'Other',
                          ]}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikSelect
                          name="severity"
                          className="rework-selection"
                          notched={false}
                          labelId="fix-selection-label"
                          testid="solicitor-dropdown"
                          MenuProps={{
                            className: 'menu-fix-selection single-fix-rework',
                          }}
                          outerLabel
                          placeholder="Select Severity"
                          notEmphasized
                          notSelectablePlaceholder
                          label="Severity"
                          items={['Low', 'Moderate', 'High', 'Critical']}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikSelect
                          name="status"
                          className="rework-selection"
                          notched={false}
                          labelId="fix-selection-label"
                          testid="solicitor-dropdown"
                          MenuProps={{
                            className: 'menu-fix-selection single-fix-rework',
                          }}
                          outerLabel
                          placeholder="Select Status"
                          notEmphasized
                          notSelectablePlaceholder
                          label="Incident Status"
                          items={[
                            'Detected',
                            'Investigating',
                            'Triaged',
                            'Assessed',
                            'Notified',
                            'Analyzed',
                            'Contained',
                            'Corrected',
                            'Remediated',
                            'Closed',
                          ]}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikTextField
                          name="business_owner"
                          placeholder="Name of responsible person"
                          label="Business Owner*"
                          outerLabel
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikSelectWithKeys
                          name="assigned_to"
                          outerLabel
                          placeholder="Assign to"
                          label="Assign To"
                          titleKey="name"
                          valueKey="userId"
                          items={teamMembers}
                        />
                        <Typography className="text-gray">
                          Select a team member to assign this incident to
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack
                          className="app-box-border"
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography className="f16 fw-5">
                              Contains Personal Data
                            </Typography>
                            <Typography className="text-gray">
                              Indicate if the incident involves personal data
                            </Typography>
                          </Box>
                          <Switch
                            checked={values.contains_personal_data}
                            onChange={(e) => {
                              setFieldValue(
                                'contains_personal_data',
                                e.target.checked
                              );
                            }}
                            defaultChecked
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  )}
                  {values.tabStatus[1] && (
                    <Grid spacing={2} container className="incident-wrapper">
                      <Grid item xs={6}>
                        <AppFormikDateTimePicker
                          name="dpo_notified_at"
                          label="DPO Notified At"
                          disablePast
                          onAccepted={() => {}}
                          outerLabel
                        />
                        <Typography className="text-gray">
                          Date and time when the Data Protection Officer was
                          notified
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikDateTimePicker
                          name="regulatory_notified_at"
                          label="Regulatory Notified At"
                          disablePast
                          onAccepted={() => {}}
                          outerLabel
                        />
                        <Typography className="text-gray">
                          Date and time when regulatory authorities were
                          notified
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        value={dayjs('2022-04-17T15:30')}
                        label="Basic date time picker"
                      />
                    </LocalizationProvider> */}
                        <AppFormikTextField
                          name="regulatory_notification_reference"
                          placeholder="Reference number or ID"
                          label="Regulatory Notification Reference"
                          outerLabel
                        />
                        <Typography className="text-gray">
                          Reference number provided by regulatory authorities
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikTextField
                          name="internal_escalation_level"
                          placeholder="Level 1,2,3 etc"
                          label="Internal Escalation Level"
                          outerLabel
                        />
                        <Typography className="text-gray">
                          Current escalation level within the organisation
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="containment_actions"
                          placeholder="Describe the actions taken to contain the incident"
                          label="Containment Actions"
                          outerLabel
                          multiple
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="harm_description"
                          placeholder="Describe the harm or potential harm caused"
                          label="Harm Description"
                          outerLabel
                          multiple
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikTextField
                          name="operational_disruption_level"
                          placeholder="None, Low, Medium, High"
                          label="Operational Disruption Level"
                          outerLabel
                        />
                        <Typography className="text-gray">
                          Level of operational disruption caused by the incident
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <AppFormikTextField
                          name="compliance_impact"
                          placeholder="GDPR, CCPA, etc."
                          label="Compliance Impact"
                          outerLabel
                        />
                        <Typography className="text-gray">
                          Compliance frameworks or regulations impacted
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {values.tabStatus[2] && (
                    <Grid spacing={2} container className="incident-wrapper">
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="root_cause"
                          placeholder="Describe the root cause of the incident"
                          label="Root Cause"
                          outerLabel
                          rows={3}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="root_cause_report"
                          placeholder="Detailed root cause analysis report"
                          label="Root Cause Report"
                          outerLabel
                          rows={3}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="corrective_actions"
                          placeholder="Describe the corrective actions taken"
                          label="Corrective Actions"
                          outerLabel
                          rows={3}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="recurrence_risk"
                          placeholder="Assessment of the risk of reoccurence"
                          label="Recurrence Risk"
                          outerLabel
                          rows={3}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="retest_results"
                          placeholder="Results of retesting after corrective actions"
                          label="Retest Results"
                          outerLabel
                          rows={3}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AppFormikTextArea
                          name="lessons_learned"
                          placeholder="Key lessons learned from this incident"
                          label="Lessons Learned"
                          outerLabel
                          rows={3}
                        />
                      </Grid>
                    </Grid>
                  )}
                  <Stack
                    spacing={2}
                    className="mt-4"
                    direction="row"
                    justifyContent="flex-end"
                  >
                    <AppPrimaryButtonOutlined
                      title="Cancel"
                      onClick={() => {
                        navigate(-1);
                      }}
                    />
                    <AppPrimaryButton
                      title={isEdit ? 'Update Incident' : 'Report Incident'}
                      onClick={handleSubmit}
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                    />
                  </Stack>
                </>
              )}
            </Formik>
          </Box>
        </>
      )}
    </>
  );
};

export default AddIncidents;
